import { Fragment, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useParams, Link } from 'react-router-dom';
import { Badge } from '@mantine/core';

import { MiniTable } from '@rr/ui';
import { formatCurrency } from '@rr/utils';

import { GET_STUDENT_PLANS_QUERY } from './GetStudentPlans.query.gql';
import { Plans, GetAdminStudentPlansQuery, Users } from 'types/graphql';

interface NameCell {
  value: string;
  cell: {
    row: {
      original: Plans | Users;
    };
  };
}

interface TeacherCell {
  value: string;
  cell: {
    row: {
      original: {
        teacherId: string;
      };
    };
  };
}

interface LessonPriceCell {
  value: number;
}

interface ActivePlanCell {
  value: boolean;
}

export const StudentPlans = () => {
  const { studentId }: { studentId: string } = useParams();
  const [offset, setOffset] = useState(0);
  const LIMIT = 5;

  useEffect(() => {
    setOffset(0);
  }, [studentId]);

  const updateOffset = (number: number) => setOffset(number);

  const { loading, error, data } = useQuery<GetAdminStudentPlansQuery>(
    GET_STUDENT_PLANS_QUERY,
    {
      variables: {
        id: studentId,
        offset,
        limit: LIMIT,
      },
    }
  );

  if (error) {
    console.error(error);
    return <div>Error</div>;
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  console.log(data);

  const PLAN_COLUMNS = [
    {
      Header: 'Plan Name',
      accessor: 'planName',
      Cell: ({ cell, value }: NameCell) => (
        // @ts-expect-error stupid
        <Link to={`/plans/${cell.row.original.planId}`}>{value}</Link>
      ),
    },
    {
      Header: 'Teacher',
      accessor: 'teacher',
      Cell: ({ value, cell }: TeacherCell) => (
        <Link to={`/teachers/${cell.row.original.teacherId}`}>{value}</Link>
      ),
    },
    {
      Header: 'Instrument',
      accessor: 'instrument',
    },
    {
      Header: 'Lesson Price',
      accessor: 'lessonPrice',
      Cell: ({ value }: LessonPriceCell) => <div>{formatCurrency(value)}</div>,
    },
    {
      Header: 'Remaining',
      accessor: 'lessonCount',
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }: ActivePlanCell) => (
        <div>
          {value ? (
            <Badge variant="dot" color="green">
              ACTIVE
            </Badge>
          ) : (
            <Badge variant="dot" color="yellow">
              Inactive
            </Badge>
          )}
        </div>
      ),
    },
  ];

  const planData =
    data?.students_by_pk?.student_plans.map(
      ({ plan, user, current_count, instrument }) => ({
        planId: plan.id,
        planName: plan.prettyName,
        teacher: user.gdpr_name,
        teacherId: user.id,
        lessonPrice: plan.lessonPrice,
        lessonCount: `${plan.lessonCount - current_count}/${plan.lessonCount}`,
        status: plan.active,
        instrument: instrument.prettyName,
      })
    ) || [];

  return planData.length !== 0 ? (
    <MiniTable.Wrapper
      columns={PLAN_COLUMNS}
      data={planData}
      offset={offset}
      count={data?.students_by_pk?.student_plans_aggregate.aggregate?.count}
      limit={LIMIT}
      updateOffset={updateOffset}
    >
      <MiniTable.Header />
      <MiniTable.Body />
      <MiniTable.Pagination />
    </MiniTable.Wrapper>
  ) : (
    <MiniTable.Zero>No active plans</MiniTable.Zero>
  );
};
